import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { map, filter, capitalize, sortBy } from 'lodash';

import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { TableColumnConfiguration } from 'src/types';
import Toggle from 'src/presentation/components/forms/Toggle';
import { toastError } from 'src/actions/toast';

import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, { allFinished, allSucceeded } from 'src/presentation/hooks/useResource';

import CreateIntegrationDropdown from 'src/presentation/components/pages/integrations/CreateIntegrationDropdown';
import IntegrationEditor from 'src/presentation/components/pages/integrations/IntegrationEditor';
import { IntegrationDisplayLabels } from 'src/presentation/components/pages/integrations/constants';
import integrationsResource, { ExternalSource } from 'src/actions/integrations/integrationsResource';
import integrationsOrgConfigResource from 'src/actions/integrations/integrationsOrgConfigResource';
import { OrgConfigContext } from 'src/presentation/components/pages/integrations/editorComponents/OrgConfigContext';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';

interface DisplayLookupSource extends ExternalSource {
  autoLinkFields: string;
}

const Integrations: TablePageRender = ({ root }): JSX.Element => {
  const dispatch = useDispatch();
  const { errors, loadingState, values } = useResource(integrationsResource);
  const orgConfig = useDocumentResource(integrationsOrgConfigResource);
  const normalizeCase = (label?: string) => capitalize((label || '').trim().toLowerCase());
  const concatLabels = (labels?: string[]) => map(labels, label => normalizeCase(label)).join(', ');
  const finished = allFinished(loadingState, orgConfig.loadingState);
  const fetchError = finished && !allSucceeded(loadingState, orgConfig.loadingState);

  const sortedIntegrations = sortBy(values, item => [normalizeCase(item.type), normalizeCase(item.name)]);

  const filterFunc = (items, searchString) => {
    if (!searchString) return items;

    const nameMatch = item => item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
    const typeMatch = item => item.type.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
    return filter(items, item => nameMatch(item) || typeMatch(item));
  };

  const pageDescription = (
    <span>
      Apps are external services and systems that Gladly may use to retrieve customer information. To learn more about
      apps, visit our{' '}
      <ExternalLink
        data-aid="developer-help"
        label="Help Docs"
        to="https://help.gladly.com/docs/introduction-to-integrations"
      />
      .
    </span>
  );
  const pageActions = <CreateIntegrationDropdown root={root} orgConfig={orgConfig?.value} />;

  const toggleEnabled = (item: ExternalSource) => {
    const toast = `App '${item.name}' is ${item.enabled ? 'disabled' : 'enabled'}`;
    dispatch(
      integrationsResource.effects.update(
        { id: item.id },
        { enabled: !item.enabled },
        {
          customToastMsg: toast,
          disableWindowBack: true,
        }
      )
    );
  };

  const renderNameColumn = (item): JSX.Element => {
    const name = (item.name || '').trim();
    return (
      <NameColumnWrapper data-aid={`source-name-wrapper-${item.id}`} title={name}>
        {name}
      </NameColumnWrapper>
    );
  };

  const columnConfig: TableColumnConfiguration<DisplayLookupSource>[] = [
    {
      attr: 'enabled',
      customRenderer: item => (
        <Toggle
          data-aid={`enable-source-${item.id}`}
          value={item.enabled}
          onChange={() => {
            toggleEnabled(item);
          }}
        />
      ),
      customStyle: () => ({ minWidth: '64px', width: '80px' }),
    },
    { attr: 'name', label: 'Name', customRenderer: item => renderNameColumn(item) },
    {
      attr: 'type',
      label: 'Type',
      customRenderer: item => IntegrationDisplayLabels[item.type],
    },
    {
      attr: 'autoLinkFields',
      label: 'Autolink Fields',
      customRenderer: item => concatLabels(item.customerLookupSettings?.autoLinkFields),
    },
  ];

  const emptyState = (
    <EmptyStateWrapper data-aid="integrations-emptyState">
      <H3 data-aid="emptyState-header">There are no configured integrations</H3>
      <Byline data-aid="emptyState-message">Click &apos;Add App&apos; to create a new integration configuration</Byline>
    </EmptyStateWrapper>
  );

  useEffect(() => {
    if (finished && fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [finished, fetchError, dispatch]);

  return (
    <OrgConfigContext.Provider value={orgConfig.value}>
      <TablePage
        errors={errors}
        isLoading={!finished}
        filterFunc={filterFunc}
        items={sortedIntegrations}
        disableSearch={!values || !values.length}
        emptyStateRender={emptyState}
        columnConfig={columnConfig}
        editor={IntegrationEditor}
        name="Apps"
        description={pageDescription}
        actions={pageActions}
        root={root}
        data-aid="integrations-admin-page"
        resetErrors={integrationsResource.actions.resetErrors}
      />
    </OrgConfigContext.Provider>
  );
};

const NameColumnWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Byline = styled.p`
  margin: 4px;
  color: ${p => p.theme.colors.gray800};
`;

export default Integrations;
